<template>
  <b-modal
    v-model="isVisible"
    title="Привязать студента к родителю"
    size="lg"
    @hidden="resetForm"
    @ok.prevent="handleSubmit"
    ok-title="Создать связь"
    cancel-title="Отмена"
    :ok-disabled="!isFormValid"
  >
    <div v-if="selectedParent">
      <!-- Parent Info -->
      <div class="alert alert-info">
        <div class="d-flex align-items-center">
          <feather-icon icon="UserIcon" size="20" class="mr-2" />
          <div>
            <strong>Родитель:</strong> {{ selectedParent.name }} ({{ selectedParent.email }})
          </div>
        </div>
      </div>

      <b-form @submit.prevent="handleSubmit">
        <!-- Student Selection -->
        <b-form-group
          label="Выберите студента для привязки"
          label-for="student-select"
        >
          <div class="position-relative">
            <b-form-input
              v-model="searchStudent"
              placeholder="Поиск студента по имени или email..."
              @input="filterStudents"
              class="mb-3"
            />
            <feather-icon
              icon="SearchIcon"
              size="16"
              class="position-absolute"
              style="top: 12px; right: 12px; color: #6c757d;"
            />
          </div>

          <!-- Students List -->
          <div class="students-list" style="max-height: 300px; overflow-y: auto;">
            <div
              v-for="student in filteredStudents"
              :key="student.id"
              class="student-option"
              :class="{'selected': form.student_id === student.id}"
              @click="selectStudent(student)"
            >
              <div class="d-flex justify-content-between align-items-center p-3 border-bottom">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center">
                    <feather-icon icon="UserIcon" size="16" class="mr-2" />
                    <div>
                      <h6 class="mb-0">{{ student.name }}</h6>
                      <small class="text-muted">{{ student.email }}</small>
                    </div>
                  </div>
                  <div class="mt-1">
                    <span class="badge badge-secondary mr-1">
                      Баланс: {{ student.balance || 0 }}
                    </span>
                    <span
                      v-if="student.has_parents"
                      class="badge badge-info"
                    >
                      Есть родители ({{ student.parents_count }})
                    </span>
                  </div>
                </div>
                <div>
                  <feather-icon
                    v-if="form.student_id === student.id"
                    icon="CheckIcon"
                    size="20"
                    class="text-success"
                  />
                </div>
              </div>
            </div>

            <!-- No results -->
            <div v-if="filteredStudents.length === 0" class="text-center p-4 text-muted">
              <feather-icon icon="UsersIcon" size="32" class="mb-2" />
              <p class="mb-0">Студенты не найдены</p>
            </div>
          </div>
        </b-form-group>

        <!-- Selected Student Info -->
        <div v-if="selectedStudent" class="alert alert-success">
          <h6 class="mb-2">Выбранный студент:</h6>
          <div class="row">
            <div class="col-md-6">
              <p class="mb-1"><strong>Имя:</strong> {{ selectedStudent.name }}</p>
              <p class="mb-1"><strong>Email:</strong> {{ selectedStudent.email }}</p>
            </div>
            <div class="col-md-6">
              <p class="mb-1"><strong>Баланс:</strong> {{ selectedStudent.balance || 0 }}</p>
              <p class="mb-0"><strong>Родителей:</strong> {{ selectedStudent.parents_count }}</p>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <b-form-group
          label="Заметки (необязательно)"
          label-for="binding-notes"
        >
          <b-form-textarea
            id="binding-notes"
            v-model="form.notes"
            placeholder="Дополнительная информация о связи..."
            rows="3"
          />
        </b-form-group>

        <!-- Warning for existing parents -->
        <div
          v-if="selectedStudent && selectedStudent.has_parents"
          class="alert alert-warning"
        >
          <div class="d-flex align-items-center">
            <feather-icon icon="AlertTriangleIcon" size="20" class="mr-2" />
            <div>
              <strong>Внимание!</strong> У этого студента уже есть {{ selectedStudent.parents_count }} родител{{ selectedStudent.parents_count === 1 ? 'ь' : 'ей' }}.
              Вы можете добавить ещё одного родителя.
            </div>
          </div>
        </div>
      </b-form>
    </div>

    <!-- No parent selected -->
    <div v-else class="text-center p-4">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-warning mb-3" />
      <h5>Родитель не выбран</h5>
      <p class="text-muted">Пожалуйста, выберите родителя в списке перед привязкой студента</p>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BAlert
} from 'bootstrap-vue'
import ParentalControlAPI from '@/api/parentalControl'

export default {
  name: 'BindingModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BAlert
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedParent: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isVisible: this.value,
      isSubmitting: false,
      searchStudent: '',
      students: [],
      filteredStudents: [],
      selectedStudent: null,
      form: {
        parent_id: null,
        student_id: null,
        notes: ''
      }
    }
  },
  computed: {
    isFormValid() {
      return this.form.parent_id && this.form.student_id
    }
  },
  watch: {
    value(newVal) {
      this.isVisible = newVal
    },
    isVisible(newVal) {
      this.$emit('input', newVal)
      if (newVal) {
        this.loadStudents()
      }
    },
    selectedParent: {
      handler(newParent) {
        if (newParent) {
          this.form.parent_id = newParent.id
        } else {
          this.form.parent_id = null
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadStudents() {
      try {
        const response = await ParentalControlAPI.getStudentsForBinding()
        this.students = response.data
        this.filteredStudents = response.data
      } catch (error) {
        this.$bvToast.toast('Ошибка загрузки студентов', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      }
    },
    filterStudents() {
      const query = this.searchStudent.toLowerCase()
      if (!query) {
        this.filteredStudents = this.students
        return
      }

      this.filteredStudents = this.students.filter(student =>
        student.name.toLowerCase().includes(query) ||
        student.email.toLowerCase().includes(query)
      )
    },
    selectStudent(student) {
      this.selectedStudent = student
      this.form.student_id = student.id
    },
    async handleSubmit() {
      if (!this.isFormValid) return

      this.isSubmitting = true

      try {
        const response = await ParentalControlAPI.bindStudent(this.form)

        this.$bvToast.toast('Студент успешно привязан к родителю', {
          title: 'Успех',
          variant: 'success',
          solid: true,
        })

        this.$emit('binding-created', response.data.relationship)
        this.resetForm()
        this.isVisible = false

      } catch (error) {
        let errorMessage = 'Ошибка при привязке студента'

        if (error.response && error.response.data.error) {
          errorMessage = error.response.data.error
        }

        this.$bvToast.toast(errorMessage, {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isSubmitting = false
      }
    },
    resetForm() {
      this.form = {
        parent_id: this.selectedParent?.id || null,
        student_id: null,
        notes: ''
      }
      this.selectedStudent = null
      this.searchStudent = ''
      this.filteredStudents = this.students
    }
  }
}
</script>

<style lang="scss" scoped>
.students-list {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;

  .student-option {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
    }

    &.selected {
      background-color: #e3f2fd;
      border-left: 4px solid #2196F3;
    }

    &:last-child .border-bottom {
      border-bottom: none !important;
    }
  }
}

.position-absolute {
  pointer-events: none;
}

.alert {
  border-radius: 0.5rem;
}

.badge {
  font-size: 0.75rem;
}

.dark-layout {
  .students-list {
    border-color: #404656;

    .student-option {
      &:hover {
        background-color: #404656;
      }

      &.selected {
        background-color: #2d3748;
        border-left-color: #667eea;
      }
    }
  }

  .alert-info {
    background-color: #1a365d;
    border-color: #2d5a87;
    color: #bee3f8;
  }

  .alert-success {
    background-color: #1a4049;
    border-color: #276749;
    color: #9ae6b4;
  }

  .alert-warning {
    background-color: #744210;
    border-color: #d69e2e;
    color: #fef5e7;
  }
}
</style>